import { useMemo } from 'react';
import { Option } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import { css } from 'styled-components';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import theme from '@/styles/theme';

export const suggestedDayCSS = css`
  border-color: ${({ theme }) => theme.colors.success} !important;
  &:hover {
    background-color: ${({ theme }) => theme.colors.successContainer} !important;
  }
`;

const MAX_SERVICE_DATES_OPTIONS = 3;

interface Props {
  dateOfService?: string;
}

const useDateOfServiceOptions = ({ dateOfService }: Props) => {
  const { ocrDocument } = useDocumentOcrStore();

  const suggestedServiceDates = useMemo(() => {
    if (!ocrDocument?.serviceDateOptions?.length) {
      return [];
    }
    const filteredServiceDates = ocrDocument?.serviceDateOptions?.filter((serviceDate) => !!serviceDate?.isSuggested && !!serviceDate?.date);
    const uniqOcrServiceDates = uniqBy(filteredServiceDates, 'date');
    return uniqOcrServiceDates;
  }, [ocrDocument?.serviceDateOptions]);

  const suggestedDateOfServiceOptions = useMemo(() => {
    const options = suggestedServiceDates
      .slice(0, MAX_SERVICE_DATES_OPTIONS)
      .map((serviceDate): Option<string> => ({
        key: dayjs(serviceDate?.date).format(DEFAULT_DATE_FORMAT),
        title: dayjs(serviceDate?.date).format(DEFAULT_DATE_FORMAT),
        value: dayjs(serviceDate?.date).format(DEFAULT_DATE_FORMAT),
      }));
    return options;
  }, [suggestedServiceDates]);

  const highlightedDates = useMemo(() => {
    return suggestedServiceDates?.map((serviceDate) => ({
      date: serviceDate?.date || '',
      labelStyle: { color: theme.colors.success },
      dayCSS: suggestedDayCSS,
    }));
  }, [suggestedServiceDates]);

  const selectedDateFromSuggested = useMemo(() => suggestedServiceDates
    ?.find((serviceDate) => {
      if (!serviceDate?.date || !dateOfService) {
        return false;
      }
      return dayjs(serviceDate?.date).format(DEFAULT_DATE_FORMAT) === dayjs(dateOfService).format(DEFAULT_DATE_FORMAT);
    }),
    [dateOfService, suggestedServiceDates]);

  return {
    suggestedDateOfServiceOptions,
    highlightedDates,
    selectedDateFromSuggested,
  };
};

export default useDateOfServiceOptions;
